import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Type, Image, Square, Columns, List, CreditCard, MessageSquare,
  GripVertical, Plus, Trash2, Save, Eye, Settings2, X, ChevronDown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

// Component types available in the builder
const COMPONENT_TYPES = [
  { id: 'hero', name: 'Hero Section', icon: Square, category: 'sections' },
  { id: 'heading', name: 'Heading', icon: Type, category: 'text' },
  { id: 'paragraph', name: 'Paragraph', icon: Type, category: 'text' },
  { id: 'image', name: 'Image', icon: Image, category: 'media' },
  { id: 'button', name: 'Button', icon: Square, category: 'interactive' },
  { id: 'features', name: 'Features Grid', icon: Columns, category: 'sections' },
  { id: 'pricing', name: 'Pricing Cards', icon: CreditCard, category: 'sections' },
  { id: 'testimonial', name: 'Testimonial', icon: MessageSquare, category: 'sections' },
  { id: 'cta', name: 'Call to Action', icon: Square, category: 'sections' },
  { id: 'list', name: 'Feature List', icon: List, category: 'content' },
];

// Default content for each component type
const DEFAULT_CONTENT = {
  hero: {
    title: 'Welcome to Flowteras',
    subtitle: 'Your WhatsApp Business Operating System',
    buttonText: 'Get Started',
    buttonLink: '/signup',
    bgColor: 'emerald',
  },
  heading: {
    text: 'Section Heading',
    level: 'h2',
    align: 'center',
  },
  paragraph: {
    text: 'Add your content here. This is a paragraph component that you can customize with your own text.',
    align: 'left',
  },
  image: {
    src: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800',
    alt: 'Image description',
    width: '100%',
  },
  button: {
    text: 'Click Me',
    link: '#',
    variant: 'primary',
  },
  features: {
    title: 'Our Features',
    items: [
      { title: 'Feature 1', description: 'Description of feature 1', icon: 'zap' },
      { title: 'Feature 2', description: 'Description of feature 2', icon: 'shield' },
      { title: 'Feature 3', description: 'Description of feature 3', icon: 'users' },
    ],
  },
  pricing: {
    title: 'Pricing Plans',
    plans: [
      { name: 'Starter', price: '$49', features: ['Feature 1', 'Feature 2'] },
      { name: 'Pro', price: '$149', features: ['Feature 1', 'Feature 2', 'Feature 3'], popular: true },
      { name: 'Enterprise', price: '$499', features: ['All features', 'Priority support'] },
    ],
  },
  testimonial: {
    quote: 'Flowteras transformed how we handle customer support.',
    author: 'John Doe',
    role: 'CEO, Company Inc.',
  },
  cta: {
    title: 'Ready to Get Started?',
    description: 'Join thousands of businesses using Flowteras',
    buttonText: 'Start Free Trial',
    buttonLink: '/signup',
  },
  list: {
    title: 'What You Get',
    items: ['Feature one', 'Feature two', 'Feature three', 'Feature four'],
  },
};

// Sortable Item Component
const SortableItem = ({ id, component, onEdit, onDelete, isSelected, onSelect }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group border-2 rounded-lg p-4 mb-3 bg-card transition-all ${
        isSelected ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-transparent hover:border-slate-300'
      }`}
      onClick={() => onSelect(id)}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute left-2 top-1/2 -translate-y-1/2 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <GripVertical className="w-5 h-5 text-slate-400" />
      </div>

      {/* Component Preview */}
      <div className="pl-8 pr-20">
        <ComponentPreview component={component} />
      </div>

      {/* Actions */}
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onEdit(id); }}>
          <Settings2 className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="icon" className="text-red-500" onClick={(e) => { e.stopPropagation(); onDelete(id); }}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      {/* Type Badge */}
      <Badge variant="outline" className="absolute top-2 right-2 text-xs opacity-50 group-hover:opacity-100">
        {component.type}
      </Badge>
    </div>
  );
};

// Component Preview Renderer
const ComponentPreview = ({ component }) => {
  const { type, content } = component;

  switch (type) {
    case 'hero':
      return (
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6 rounded-lg text-center">
          <h1 className="text-2xl font-bold mb-2">{content.title}</h1>
          <p className="text-emerald-100 mb-4">{content.subtitle}</p>
          <button className="bg-white text-emerald-700 px-4 py-2 rounded-full text-sm font-medium">
            {content.buttonText}
          </button>
        </div>
      );
    case 'heading':
      const HeadingTag = content.level || 'h2';
      return (
        <HeadingTag className={`font-bold text-${content.align} ${content.level === 'h1' ? 'text-3xl' : content.level === 'h2' ? 'text-2xl' : 'text-xl'}`}>
          {content.text}
        </HeadingTag>
      );
    case 'paragraph':
      return <p className={`text-slate-600 text-${content.align}`}>{content.text}</p>;
    case 'image':
      return (
        <img
          src={content.src}
          alt={content.alt}
          className="rounded-lg max-h-48 object-cover mx-auto"
          style={{ width: content.width }}
        />
      );
    case 'button':
      return (
        <button className={`px-6 py-2 rounded-full font-medium ${
          content.variant === 'primary' ? 'bg-emerald-700 text-white' : 'border border-slate-300'
        }`}>
          {content.text}
        </button>
      );
    case 'features':
      return (
        <div>
          <h3 className="text-xl font-bold text-center mb-4">{content.title}</h3>
          <div className="grid grid-cols-3 gap-4">
            {content.items?.map((item, i) => (
              <div key={i} className="text-center p-3 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg mx-auto mb-2" />
                <p className="font-medium text-sm">{item.title}</p>
              </div>
            ))}
          </div>
        </div>
      );
    case 'pricing':
      return (
        <div>
          <h3 className="text-xl font-bold text-center mb-4">{content.title}</h3>
          <div className="grid grid-cols-3 gap-3">
            {content.plans?.map((plan, i) => (
              <div key={i} className={`p-3 rounded-lg border ${plan.popular ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'}`}>
                <p className="font-medium">{plan.name}</p>
                <p className="text-xl font-bold">{plan.price}</p>
              </div>
            ))}
          </div>
        </div>
      );
    case 'testimonial':
      return (
        <div className="bg-slate-50 p-4 rounded-lg italic">
          <p className="text-slate-600 mb-2">"{content.quote}"</p>
          <p className="text-sm font-medium">— {content.author}, {content.role}</p>
        </div>
      );
    case 'cta':
      return (
        <div className="bg-slate-900 text-white p-6 rounded-lg text-center">
          <h3 className="text-xl font-bold mb-2">{content.title}</h3>
          <p className="text-slate-300 mb-4 text-sm">{content.description}</p>
          <button className="bg-emerald-500 px-4 py-2 rounded-full text-sm font-medium">
            {content.buttonText}
          </button>
        </div>
      );
    case 'list':
      return (
        <div>
          <h3 className="font-bold mb-2">{content.title}</h3>
          <ul className="space-y-1">
            {content.items?.map((item, i) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 rounded-full bg-emerald-500" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      );
    default:
      return <div className="text-slate-400">Unknown component type</div>;
  }
};

// Component Editor
const ComponentEditor = ({ component, onSave, onClose }) => {
  const [content, setContent] = useState(component.content);

  const updateContent = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
  };

  const renderEditor = () => {
    switch (component.type) {
      case 'hero':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={content.title} onChange={(e) => updateContent('title', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Subtitle</Label>
              <Textarea value={content.subtitle} onChange={(e) => updateContent('subtitle', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Button Text</Label>
              <Input value={content.buttonText} onChange={(e) => updateContent('buttonText', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Button Link</Label>
              <Input value={content.buttonLink} onChange={(e) => updateContent('buttonLink', e.target.value)} />
            </div>
          </div>
        );
      case 'heading':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Text</Label>
              <Input value={content.text} onChange={(e) => updateContent('text', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Level</Label>
              <Select value={content.level} onValueChange={(v) => updateContent('level', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="h1">H1 - Large</SelectItem>
                  <SelectItem value="h2">H2 - Medium</SelectItem>
                  <SelectItem value="h3">H3 - Small</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Alignment</Label>
              <Select value={content.align} onValueChange={(v) => updateContent('align', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      case 'paragraph':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Text</Label>
              <Textarea value={content.text} onChange={(e) => updateContent('text', e.target.value)} rows={5} />
            </div>
            <div className="space-y-2">
              <Label>Alignment</Label>
              <Select value={content.align} onValueChange={(v) => updateContent('align', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      case 'image':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Image URL</Label>
              <Input value={content.src} onChange={(e) => updateContent('src', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Alt Text</Label>
              <Input value={content.alt} onChange={(e) => updateContent('alt', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Width</Label>
              <Input value={content.width} onChange={(e) => updateContent('width', e.target.value)} placeholder="e.g., 100%, 500px" />
            </div>
          </div>
        );
      case 'button':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Button Text</Label>
              <Input value={content.text} onChange={(e) => updateContent('text', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Link</Label>
              <Input value={content.link} onChange={(e) => updateContent('link', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Style</Label>
              <Select value={content.variant} onValueChange={(v) => updateContent('variant', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">Primary (Filled)</SelectItem>
                  <SelectItem value="secondary">Secondary (Outline)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      case 'testimonial':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Quote</Label>
              <Textarea value={content.quote} onChange={(e) => updateContent('quote', e.target.value)} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Author Name</Label>
              <Input value={content.author} onChange={(e) => updateContent('author', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Author Role</Label>
              <Input value={content.role} onChange={(e) => updateContent('role', e.target.value)} />
            </div>
          </div>
        );
      case 'cta':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={content.title} onChange={(e) => updateContent('title', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={content.description} onChange={(e) => updateContent('description', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Button Text</Label>
              <Input value={content.buttonText} onChange={(e) => updateContent('buttonText', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Button Link</Label>
              <Input value={content.buttonLink} onChange={(e) => updateContent('buttonLink', e.target.value)} />
            </div>
          </div>
        );
      default:
        return <p className="text-muted-foreground">No editor available for this component type.</p>;
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit {component.type.charAt(0).toUpperCase() + component.type.slice(1)}</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          {renderEditor()}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={() => { onSave({ ...component, content }); onClose(); }}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Page Builder Component
const PageBuilderPage = () => {
  const [pages, setPages] = useState([
    { id: 'landing', name: 'Landing Page', path: '/' },
    { id: 'product', name: 'Product Page', path: '/product' },
    { id: 'pricing', name: 'Pricing Page', path: '/pricing' },
  ]);
  const [selectedPage, setSelectedPage] = useState('landing');
  const [components, setComponents] = useState([]);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [editingComponent, setEditingComponent] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Load page components from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(`pageBuilder_${selectedPage}`);
    if (saved) {
      setComponents(JSON.parse(saved));
    } else {
      // Default components for landing page
      if (selectedPage === 'landing') {
        setComponents([
          { id: '1', type: 'hero', content: DEFAULT_CONTENT.hero },
          { id: '2', type: 'features', content: DEFAULT_CONTENT.features },
          { id: '3', type: 'cta', content: DEFAULT_CONTENT.cta },
        ]);
      } else {
        setComponents([]);
      }
    }
  }, [selectedPage]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setComponents((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const addComponent = (type) => {
    const newComponent = {
      id: Date.now().toString(),
      type,
      content: { ...DEFAULT_CONTENT[type] },
    };
    setComponents([...components, newComponent]);
    toast.success(`Added ${type} component`);
  };

  const deleteComponent = (id) => {
    setComponents(components.filter((c) => c.id !== id));
    if (selectedComponent === id) setSelectedComponent(null);
    toast.success('Component deleted');
  };

  const updateComponent = (updated) => {
    setComponents(components.map((c) => (c.id === updated.id ? updated : c)));
    toast.success('Component updated');
  };

  const savePage = () => {
    localStorage.setItem(`pageBuilder_${selectedPage}`, JSON.stringify(components));
    toast.success('Page saved successfully!');
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="page-builder">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">Page Builder</h1>
          <p className="text-muted-foreground">Drag and drop to customize your pages</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedPage} onValueChange={setSelectedPage}>
            <SelectTrigger className="w-48" data-testid="page-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pages.map((page) => (
                <SelectItem key={page.id} value={page.id}>{page.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setPreviewMode(!previewMode)} data-testid="preview-btn">
            <Eye className="w-4 h-4 mr-2" />
            {previewMode ? 'Edit' : 'Preview'}
          </Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={savePage} data-testid="save-page-btn">
            <Save className="w-4 h-4 mr-2" />
            Save Page
          </Button>
        </div>
      </div>

      <div className="flex-1 flex gap-4 overflow-hidden">
        {/* Component Palette */}
        {!previewMode && (
          <Card className="w-64 flex-shrink-0 overflow-hidden">
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Components</CardTitle>
            </CardHeader>
            <CardContent className="p-2 overflow-y-auto max-h-[calc(100vh-16rem)]">
              <div className="space-y-1">
                {COMPONENT_TYPES.map((comp) => (
                  <button
                    key={comp.id}
                    onClick={() => addComponent(comp.id)}
                    className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-muted text-left text-sm transition-colors"
                    data-testid={`add-${comp.id}`}
                  >
                    <comp.icon className="w-4 h-4 text-emerald-600" />
                    {comp.name}
                    <Plus className="w-3 h-3 ml-auto text-muted-foreground" />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Canvas */}
        <div className={`flex-1 bg-slate-50 dark:bg-slate-900 rounded-xl border overflow-y-auto p-4 ${previewMode ? '' : ''}`}>
          {previewMode ? (
            // Preview Mode
            <div className="max-w-4xl mx-auto space-y-6">
              {components.map((comp) => (
                <ComponentPreview key={comp.id} component={comp} />
              ))}
            </div>
          ) : (
            // Edit Mode
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={components.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                <div className="max-w-4xl mx-auto">
                  {components.length === 0 ? (
                    <div className="text-center py-20 text-muted-foreground">
                      <Square className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>No components added yet</p>
                      <p className="text-sm">Click a component from the left panel to add it</p>
                    </div>
                  ) : (
                    components.map((comp) => (
                      <SortableItem
                        key={comp.id}
                        id={comp.id}
                        component={comp}
                        isSelected={selectedComponent === comp.id}
                        onSelect={setSelectedComponent}
                        onEdit={setEditingComponent}
                        onDelete={deleteComponent}
                      />
                    ))
                  )}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      {/* Component Editor Dialog */}
      {editingComponent && (
        <ComponentEditor
          component={components.find((c) => c.id === editingComponent)}
          onSave={updateComponent}
          onClose={() => setEditingComponent(null)}
        />
      )}
    </div>
  );
};

export default PageBuilderPage;
